import React, { useContext, useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { Link } from "react-router-dom";
import API from "../utils/API";
import { AuthContext } from "../utils/AuthContext";
import AddTaskModal from "../components/AddTaskModal";
import CreateFeedbackRequestModal from "../components/CreateFeedbackRequestModal";
import PageTitle from "../components/PageTitle";
import TaskDetailModal from "../components/TaskDetailModal";
import RulemakingTasks from "../components/RulemakingTasks";
import { buildHearingEvent, buildRulemakingEvent, downloadICS, googleCalendarUrl } from "../utils/calendarExport";
import { deriveBillDisplayFields } from "../utils/billDisplay";
import "./dashboard.css";

// ── Helpers ──────────────────────────────────────────────────────────────────

const _ptDay = new Date().toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'long' });
const _useThreeDay = _ptDay === 'Sunday' || _ptDay === 'Monday';

const TAG_COLORS = {
  purple: { bg: '#e8e4f4', text: '#3d2e7a' },
  teal:   { bg: '#cfe8ef', text: '#1a4a5a' },
  sage:   { bg: '#d5eddf', text: '#1a4a2e' },
  amber:  { bg: '#fdebd0', text: '#6b3d0a' },
  rose:   { bg: '#fcd9e2', text: '#6b1a2e' },
  slate:  { bg: '#dde3ec', text: '#2b3a4c' },
  indigo: { bg: '#dce0f5', text: '#2d2a7e' },
  coral:  { bg: '#fce0d8', text: '#6b2a1a' },
};
const DEFAULT_TEAM_TAG = { bg: '#e8e4f4', text: '#3d2e7a' };

const METRICS = [
  { key: "active", label: "Active", dot: "dot-active", row: "metric-row-active",
    tooltip: (<><strong>Active</strong> bills are current-session bills still in progress — not yet Chaptered, Vetoed, Signed by Governor, Died, Failed, or Failed Passage in Committee, and with no history action containing "held under submission" or "died." During interim recess or while bills sit with the Governor after floor adjournment, this narrows to just bills with status Enrolled, since nothing else is moving.</>) },
  { key: _useThreeDay ? "three_day_introduced" : "daily_introduced", label: _useThreeDay ? "3-Day Introduced" : "Daily Introduced", dot: "dot-daily-intro", row: "metric-row-daily-intro" },
  { key: _useThreeDay ? "three_day_amended" : "daily_amended", label: _useThreeDay ? "3-Day Amended" : "Daily Amended", dot: "dot-daily-amend", row: "metric-row-daily-amend" },
  { key: "passed_enrolled", label: "Passed / Enrolled", dot: "dot-passed-enrolled", row: "metric-row-passed-enrolled" },
  { key: "awaiting_governor", label: "Awaiting Governor", dot: "dot-awaiting-governor", row: "metric-row-awaiting-governor",
    tooltip: (<><strong>Awaiting Governor</strong> bills are current-session bills with status Enrolled — meaning they've passed both the Assembly and Senate and been sent to the Governor's desk for signature, veto, or chaptering without signature.</>) },
  { key: "chaptered", label: "Chaptered", dot: "dot-chaptered", row: "metric-row-chaptered" },
  { key: "vetoed", label: "Vetoed", dot: "dot-vetoed", row: "metric-row-vetoed" },
];

const BILL_TYPE_ORDER = ["AB", "SB", "ACA", "SCA", "ACR", "SCR", "AJR", "SJR", "HR", "SR"];

const CLIENT_LABEL_TYPES = ['Lobbyists', 'Political Firms', 'PR Firms', 'Other'];

const LOWERCASE_WORDS = new Set([
  'a', 'an', 'the',
  'and', 'but', 'or', 'nor', 'for', 'yet', 'so',
  'at', 'by', 'in', 'of', 'on', 'to', 'up', 'as', 'if',
]);

function toTitleCase(str) {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(' ')
    .map((word, i, arr) => {
      if (!word) return word;
      // Always capitalise first and last word; lowercase the rest if in the set
      if (i === 0 || i === arr.length - 1 || !LOWERCASE_WORDS.has(word)) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    })
    .join(' ');
}

function formatTime(timeStr) {
  if (!timeStr) return "";
  const s = timeStr.trim();
  const match = s.match(/^(\d{1,2})(?::(\d{2}))?/);
  if (!match) return s;
  let h = parseInt(match[1], 10);
  const m = match[2] ?? '00';
  const lower = s.toLowerCase();
  const isPm = lower.includes('p.m.') || lower.includes('pm');
  const isAm = lower.includes('a.m.') || lower.includes('am');
  if (isPm && h < 12) h += 12;
  if (isAm && h === 12) h = 0;
  const ampm = h < 12 ? 'am' : 'pm';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
}

// Agency body names ("CPUC Voting Meeting", "Board of Registered Nursing - Board Meeting") are already
// capitalised the way the agency writes them; title-casing would turn "CPUC" into "Cpuc".
function hearingTitle(hearing) {
  return hearing.category === 'agency' ? hearing.name : toTitleCase(hearing.name);
}

function isFloorSession(hearing) {
  const desc = (hearing.name || "").toLowerCase();
  return desc.includes("assembly floor") || desc.includes("senate floor");
}

function parseFileNum(loc) {
  if (!loc) return 999999;
  const num = parseInt(loc.replace(/^A-\s*/i, ''), 10);
  return isNaN(num) ? 999999 : num;
}

function todayLabel() {
  return new Date().toLocaleDateString("en-US", {
    timeZone: "America/Los_Angeles",
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function toAcronym(name) {
  return (name || '').split(/\s+/).filter(Boolean).map(w => w[0]).join('').toUpperCase();
}

const FAILED_STATUSES = ['Died', 'Failed', 'Failed Passage in Committee'];
const TODAY_PT_DATE = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

function hasHearingToday(bill) {
  return (bill.CommitteeHearings || []).some((h) =>
    h.committee_code === bill.location && (h.hearing_date || '').split('T')[0] === TODAY_PT_DATE
  );
}

// Status categories for the Tracked Bills widget's filter dropdown — same buckets
// trackedBillStatusInfo() classifies bills into, in the order they're offered.
const TRACKED_BILL_STATUS_OPTIONS = [
  { key: 'chaptered', label: 'Chaptered' },
  { key: 'signed', label: 'Signed' },
  { key: 'vetoed', label: 'Vetoed' },
  { key: 'failed', label: 'Failed / Died' },
  { key: 'enrolled', label: 'Enrolled' },
  { key: 'hearing_today', label: 'Hearing Today' },
  { key: 'floor', label: 'On Floor' },
  { key: 'committee', label: 'In Committee' },
];

// One-line "where is this bill right now" summary for the Tracked Bills widget —
// built from the same status/location fields Legislation/Reports already derive
// via billDisplay.js, so it stays consistent with how the rest of the app labels bills.
// No em dashes in the text (product preference); a period or colon separates
// the status from its detail instead.
function trackedBillStatusInfo(bill) {
  const { status, location, fileLocationDisplay, isEnrolled } = deriveBillDisplayFields(bill);

  if (status === 'Chaptered') {
    const chapterSuffix = bill.chapter_number && bill.chapter_year
      ? `: Ch. ${bill.chapter_number} (${bill.chapter_year})`
      : '';
    return { category: 'chaptered', text: `Chaptered${chapterSuffix}`, className: 'tracked-bill-status--success' };
  }
  if (status === 'Signed by Governor') {
    return { category: 'signed', text: 'Signed', className: 'tracked-bill-status--success' };
  }
  if (status === 'Vetoed') {
    return { category: 'vetoed', text: 'Vetoed', className: 'tracked-bill-status--danger' };
  }
  if (FAILED_STATUSES.includes(status)) {
    return { category: 'failed', text: 'Failed / Died', className: 'tracked-bill-status--muted' };
  }
  if (isEnrolled) {
    return { category: 'enrolled', text: 'Enrolled: Awaiting Governor', className: 'tracked-bill-status--info' };
  }
  if (hasHearingToday(bill)) {
    return { category: 'hearing_today', text: location ? `Hearing Today: ${location}` : 'Hearing Today', className: 'tracked-bill-status--warning' };
  }
  if (location && /floor/i.test(location)) {
    const chamber = /assembly/i.test(location) ? 'Assembly ' : /senate/i.test(location) ? 'Senate ' : '';
    const filePart = fileLocationDisplay ? `: ${fileLocationDisplay}` : '';
    return { category: 'floor', text: `On ${chamber}Floor${filePart}`, className: 'tracked-bill-status--primary' };
  }
  // Naming the committee already implies "in committee," so skip the redundant prefix.
  if (location) {
    return { category: 'committee', text: location, className: 'tracked-bill-status--secondary' };
  }
  return { category: 'committee', text: status || 'In Committee', className: 'tracked-bill-status--secondary' };
}

const TRACKED_BILL_SORT_OPTIONS = [
  { key: 'recent', label: 'Recent' },
  { key: 'bill_number', label: 'Bill #' },
];

function sortTrackedBills(bills, sortMode) {
  if (sortMode === 'bill_number') {
    const sorted = [...bills];
    sorted.sort((a, b) => {
      const typeCompare = (a.bill_type || '').localeCompare(b.bill_type || '');
      if (typeCompare !== 0) return typeCompare;
      return (a.bill_number || 0) - (b.bill_number || 0);
    });
    return sorted;
  }
  // 'recent' (default): the API already returns bills in this order — most recent
  // bill_history action first (see routes/api/dashboard.js tracked-bills route).
  return bills;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SkeletonRows({ count = 4 }) {
  return Array.from({ length: count }).map((_, i) => (
    <div key={i} className="dashboard-skeleton-bar" style={{ width: `${70 + (i % 3) * 10}%`, opacity: 1 - i * 0.15 }} />
  ));
}

function LegislativeCounts({ data, loading, tracking, fixedHeight }) {
  const { individualSubscription } = useContext(AuthContext);
  const [hiddenKeys, setHiddenKeys] = useState(new Set());
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [selectedMetricKey, setSelectedMetricKey] = useState(METRICS[0].key);
  const [showGeneral, setShowGeneral] = useState(true);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Filter to only bill types with any bills at all, ordered by preference. Based on
  // total_count rather than active — after sine die, active can legitimately hit 0 for
  // every bill type at once (everything's resolved), which would otherwise wipe every
  // column instead of just leaving the Active row empty.
  const billTypes = BILL_TYPE_ORDER.filter(
    (bt) => data.some((r) => r.bill_type === bt && Number(r.total_count) > 0)
  );
  // Also include any types returned by the API that aren't in our ordered list — kept on
  // "active" (not total_count) since this is a catch-all for genuinely emerging bill types,
  // not a place for stray data artifacts (e.g. a stale 'GRP' placeholder row) to leak in.
  const extraTypes = data
    .map((r) => r.bill_type)
    .filter((bt) => !BILL_TYPE_ORDER.includes(bt) && data.find((r) => r.bill_type === bt && Number(r.active) > 0));
  const allTypes = [...billTypes, ...extraTypes];

  const getVal = (billType, metricKey) => {
    const row = data.find((r) => r.bill_type === billType);
    return row ? Number(row[metricKey]) : 0;
  };

  const hasTeams = tracking?.teams?.length > 0;
  const PERSONAL_COLOR = { bg: '#d0e8f0', text: '#1a4060' };

  // Chips: one per personal (individual-subscribers only) + team + client-group-per-team
  const chipItems = tracking ? [
    ...(individualSubscription ? [{ key: 'personal', label: hasTeams ? 'My Tracked' : 'Tracked', color: PERSONAL_COLOR }] : []),
    ...(tracking.teams || []).flatMap((t) => {
      const color = TAG_COLORS[t.tag_color] || DEFAULT_TEAM_TAG;
      const chipLabel = t.team_acronym || toAcronym(t.team_name);
      const chips = [{ key: `team_${t.team_id}`, label: chipLabel, color }];
      if (t.clients && t.clients.length > 0) {
        const isClientType = CLIENT_LABEL_TYPES.includes(t.group_type);
        chips.push({ key: `clientgroup_${t.team_id}`, label: isClientType ? 'Clients' : 'Stakeholders', color });
      }
      return chips;
    }),
  ] : [];

  // Data rows: personal (individual-subscribers only) + team + one row per individual client/stakeholder
  const trackingRows = tracking ? [
    ...(individualSubscription ? [{ key: 'personal', groupKey: null, label: hasTeams ? 'My Tracked' : 'Tracked', counts: tracking.personal || {}, isClient: false, color: PERSONAL_COLOR }] : []),
    ...(tracking.teams || []).flatMap((t) => {
      const color = TAG_COLORS[t.tag_color] || DEFAULT_TEAM_TAG;
      const rows = [{ key: `team_${t.team_id}`, groupKey: null, label: t.team_name, counts: t.counts || {}, isClient: false, color }];
      if (t.clients && t.clients.length > 0) {
        const groupKey = `clientgroup_${t.team_id}`;
        rows.push(...t.clients.map(c => ({
          key: `client_${t.team_id}_${c.client_id}`,
          groupKey,
          label: c.client_name,
          counts: c.counts || {},
          isClient: true,
          color,
        })));
      }
      return rows;
    }),
  ] : [];

  const toggleRow = (key) => setHiddenKeys(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const visibleRows = trackingRows.filter(r => {
    if (hiddenKeys.has(r.key)) return false;
    if (r.groupKey && hiddenKeys.has(r.groupKey)) return false;
    return true;
  });

  const colGroup = (
    <colgroup>
      <col style={{ width: '260px' }} />
      {allTypes.map((bt) => <col key={bt} />)}
      <col style={{ width: '62px' }} />
    </colgroup>
  );

  const renderRow = (row) => {
    const total = allTypes.reduce((sum, bt) => sum + (row.counts[bt] || 0), 0);
    const accentColor = row.color.bg;
    return (
      <tr key={row.key} className={row.isClient ? 'metric-row-client' : 'metric-row-tracked'}>
        <td className={`metric-label${row.isClient ? ' metric-label--sub' : ''}`}
            style={{ borderLeftColor: accentColor }}>
          <span className="metric-label-text">
            <span className={`metric-dot ${row.isClient ? 'dot-client' : 'dot-tracked'}`}
                  style={{ backgroundColor: accentColor }}></span>
            {row.label}
          </span>
        </td>
        {allTypes.map((bt) => {
          const val = row.counts[bt] || 0;
          return (
            <td key={bt} className={val === 0 ? "zero" : ""}>{val === 0 ? "—" : val.toLocaleString()}</td>
          );
        })}
        <td className={`total-col${total === 0 ? " zero" : ""}`}>{total === 0 ? "—" : total.toLocaleString()}</td>
      </tr>
    );
  };

  // Mobile: a single dropdown-selected metric/tracked-group shown as one value per bill type,
  // instead of the desktop's multi-column table + separate "Tracked Bills" sub-table.
  const mobileOptions = [
    ...METRICS.map((m) => ({ key: m.key, label: m.label, kind: 'metric', tooltip: m.tooltip })),
    ...(individualSubscription && tracking ? [{ key: 'personal', label: hasTeams ? 'My Tracked' : 'Tracked', kind: 'tracked', counts: tracking.personal || {} }] : []),
    ...(tracking?.teams || []).flatMap((t) => {
      const opts = [{ key: `team_${t.team_id}`, label: t.team_name, kind: 'tracked', counts: t.counts || {} }];
      if (t.clients && t.clients.length > 0) {
        opts.push(...t.clients.map((c) => ({
          key: `client_${t.team_id}_${c.client_id}`,
          label: c.client_name,
          kind: 'tracked',
          counts: c.counts || {},
        })));
      }
      return opts;
    }),
  ];
  const selectedOption = mobileOptions.find((o) => o.key === selectedMetricKey) || mobileOptions[0];
  const getMobileVal = (bt) => {
    if (!selectedOption) return 0;
    return selectedOption.kind === 'metric' ? getVal(bt, selectedOption.key) : (selectedOption.counts[bt] || 0);
  };
  const mobileTotal = allTypes.reduce((sum, bt) => sum + getMobileVal(bt), 0);

  return (
    <div className={`dashboard-card dashboard-card--counts${fixedHeight ? ' dashboard-card--fixed' : ' h-100'}`}>
      <div className="dashboard-card-header">
        <i className="bi bi-bar-chart-line-fill"></i>
        <h6>Legislative Activity</h6>
        {isMobileView && selectedOption?.tooltip && (
          <span className="eg-tooltip-wrap legislative-activity-tooltip ms-1">
            <i className="bi bi-info-circle eg-tooltip-icon" />
            <div className="eg-tooltip">{selectedOption.tooltip}</div>
          </span>
        )}
        {isMobileView ? (
          <select
            className="mobile-metrics-select ms-auto"
            value={selectedOption?.key || ''}
            onChange={(e) => setSelectedMetricKey(e.target.value)}
          >
            {mobileOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        ) : (
          <div className="tracking-header-chips ms-auto">
            <button
              className={`tracking-filter-chip ${showGeneral ? 'tracking-filter-chip--on' : 'tracking-filter-chip--off'}`}
              onClick={() => setShowGeneral(v => !v)}
            >
              General
            </button>
            {chipItems.map(chip => {
              const isOn = !hiddenKeys.has(chip.key);
              const chipStyle = isOn ? { background: chip.color.bg, borderColor: chip.color.bg, color: chip.color.text } : {};
              return (
                <button
                  key={chip.key}
                  className={`tracking-filter-chip ${isOn ? 'tracking-filter-chip--on' : 'tracking-filter-chip--off'}`}
                  style={chipStyle}
                  onClick={() => toggleRow(chip.key)}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="counts-scroll-wrapper p-0">
        {loading ? (
          <div className="p-3"><SkeletonRows count={5} /></div>
        ) : isMobileView ? (
          <ul className="mobile-metrics-list list-unstyled mb-0">
            {allTypes.map((bt) => {
              const val = getMobileVal(bt);
              return (
                <li key={bt} className="mobile-metrics-row">
                  <span className="mobile-metrics-bt">{bt}</span>
                  <span className={`mobile-metrics-value${val === 0 ? ' zero' : ''}`}>{val === 0 ? "—" : val.toLocaleString()}</span>
                </li>
              );
            })}
            <li className="mobile-metrics-row mobile-metrics-total">
              <span className="mobile-metrics-bt">Total</span>
              <span className={`mobile-metrics-value${mobileTotal === 0 ? ' zero' : ''}`}>{mobileTotal === 0 ? "—" : mobileTotal.toLocaleString()}</span>
            </li>
          </ul>
        ) : (
          // A single table (rather than two stacked tables) so the sticky <thead>'s
          // containing block spans the whole scroll range — otherwise the header
          // stops sticking as soon as the short metrics table scrolls out of view.
          <table className="counts-table counts-table--fixed">
            {colGroup}
            <thead>
              <tr>
                <th>Metric</th>
                {allTypes.map((bt) => <th key={bt}>{bt}</th>)}
                <th className="total-col">Total</th>
              </tr>
            </thead>
            <tbody>
              {showGeneral && METRICS.map((m) => {
                const total = allTypes.reduce((sum, bt) => sum + getVal(bt, m.key), 0);
                return (
                  <tr key={m.key} className={m.row}>
                    <td className="metric-label">
                      <span className="metric-label-text">
                        <span className={`metric-dot ${m.dot}`}></span>
                        {m.label}
                      </span>
                      {m.tooltip && (
                        <span className="eg-tooltip-wrap legislative-activity-tooltip ms-1">
                          <i className="bi bi-info-circle eg-tooltip-icon" />
                          <div className="eg-tooltip">{m.tooltip}</div>
                        </span>
                      )}
                    </td>
                    {allTypes.map((bt) => {
                      const val = getVal(bt, m.key);
                      return (
                        <td key={bt} className={val === 0 ? "zero" : ""}>{val === 0 ? "—" : val.toLocaleString()}</td>
                      );
                    })}
                    <td className={`total-col${total === 0 ? " zero" : ""}`}>{total === 0 ? "—" : total.toLocaleString()}</td>
                  </tr>
                );
              })}
              {!showGeneral && chipItems.length === 0 && (
                <tr>
                  <td className="metric-label metric-label--empty" colSpan={allTypes.length + 2}>
                    General metrics are hidden and there are no tracked bills to show.
                  </td>
                </tr>
              )}
              {chipItems.length > 0 && (
                <tr className="tracking-separator-row">
                  <td className="tracking-separator-cell" colSpan={allTypes.length + 2}>
                    <span className="tracking-separator-label">Tracked Bills</span>
                  </td>
                </tr>
              )}
              {visibleRows.map(renderRow)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CalendarAddButton({ hearing, hearingDate, trackedBills, teamTrackedBills }) {
  const [open, setOpen] = useState(false);

  const handleGoogle = () => {
    const event = buildHearingEvent(hearing, hearingDate, trackedBills, teamTrackedBills);
    window.open(googleCalendarUrl(event), '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const handleICS = () => {
    const event = buildHearingEvent(hearing, hearingDate, trackedBills, teamTrackedBills);
    const dateLabel = hearingDate.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
    downloadICS(event, `${hearingTitle(hearing)} - ${dateLabel}`);
    setOpen(false);
  };

  return (
    <div className="hearing-calendar-wrap">
      <button
        type="button"
        className="hearing-calendar-toggle"
        title="Add to Calendar"
        onClick={() => setOpen((o) => !o)}
      >
        <i className="bi bi-calendar-plus"></i>
      </button>
      {open && (
        <>
          <div className="hearing-calendar-overlay" onClick={() => setOpen(false)} />
          <div className="hearing-calendar-menu">
            <button type="button" onClick={handleGoogle}>
              <i className="bi bi-google"></i> Google Calendar
            </button>
            <button type="button" onClick={handleICS}>
              <i className="bi bi-calendar2-event"></i> Apple / Outlook (.ics)
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function HearingAgendaModal({ hearing, hearingDate, onClose, trackedBills, teamTrackedBills }) {
  if (!hearing) return null;

  const floor = isFloorSession(hearing);
  const location = hearing.location || hearing.loc_line_1 || "";

  const name = (hearing.name || '').toLowerCase();
  const chamber = hearing.chamber
    || (name.includes('assembly') || name.startsWith('asm') ? 'assembly' : null)
    || (name.includes('senate') || name.startsWith('sen') ? 'senate' : null);

  return (
    <div className="hearing-modal-overlay" onClick={onClose}>
      <div className="hearing-modal" onClick={(e) => e.stopPropagation()}>
        <div className={`hearing-modal-header${chamber ? ` hearing-modal-header--${chamber.toLowerCase()}` : ''}`}>
          <div className="hearing-modal-title">
            <span>{hearingTitle(hearing)}</span>
          </div>
          <div className="hearing-modal-header-actions">
            <CalendarAddButton
              hearing={hearing}
              hearingDate={hearingDate}
              trackedBills={trackedBills}
              teamTrackedBills={teamTrackedBills}
            />
            <button className="hearing-modal-close" onClick={onClose}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
        <div className="hearing-modal-meta">
          {hearingDate && <span><i className="bi bi-calendar3 me-1"></i>{hearingDate.toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", weekday: "short", month: "short", day: "numeric", year: "numeric" })}</span>}
          {hearing.agenda_time && <span><i className="bi bi-clock me-1"></i>{formatTime(hearing.agenda_time)}</span>}
          {location && <span><i className="bi bi-geo-alt me-1"></i>{location}</span>}
          {hearing.chair && <span><i className="bi bi-person me-1"></i>{hearing.chair}</span>}
        </div>
        <ul className="hearing-modal-list">
          {hearing.sessions && hearing.sessions.length > 0 ? (
            hearing.sessions.map((session, i) => (
              <li key={i} className="hearing-modal-item">
                <span className="hearing-modal-item-main">
                  <span className="hearing-agenda-code">{session.name}</span>
                  <span className="hearing-session-time">
                    {session.time ? formatTime(session.time) : 'Time TBA'}
                  </span>
                  <span className="hearing-session-links">
                    {session.links.map((link, li) => (
                      <a key={li} className="hearing-agenda-link" href={link.url} target="_blank" rel="noopener noreferrer">
                        {link.label} <i className="bi bi-box-arrow-up-right"></i>
                      </a>
                    ))}
                  </span>
                </span>
              </li>
            ))
          ) : floor ? (
            [...hearing.items]
              .sort((a, b) => {
                const aLoc = a.file_location || '';
                const bLoc = b.file_location || '';
                const aInactive = aLoc.toUpperCase().startsWith('A-');
                const bInactive = bLoc.toUpperCase().startsWith('A-');
                if (aInactive !== bInactive) return aInactive ? 1 : -1;
                return parseFileNum(aLoc) - parseFileNum(bLoc);
              })
              .map((item, i) => {
                const isTeamTracked = item.bill_id && teamTrackedBills?.has(item.bill_id);
                const isPersonallyTracked = item.bill_id && trackedBills?.has(item.bill_id);
                const teamLabels = isTeamTracked ? teamTrackedBills.get(item.bill_id) : [];
                return (
                  <li key={i} className="hearing-modal-item">
                    <span className="hearing-modal-item-main">
                      <span className="hearing-floor-file-label">File #{item.file_location}</span>
                      {' -- '}
                      {item.bill_id ? (
                        <Link to={`/legislation/bill/${item.bill_id}`} className="hearing-agenda-link" onClick={onClose}>
                          {item.item_code}
                        </Link>
                      ) : (
                        <span className="hearing-agenda-code">{item.item_code}</span>
                      )}
                      {item.description && `${item.description}`}
                      {item.floor_manager && <em> (Floor Manager: {item.floor_manager})</em>}
                    </span>
                    {(isPersonallyTracked || isTeamTracked) && (
                      <span className="hearing-track-tags">
                        {isPersonallyTracked && (
                          <span className="hearing-track-tag hearing-track-personal">Personal</span>
                        )}
                        {teamLabels.map(({ label, color }, ti) => {
                          const s = TAG_COLORS[color] || DEFAULT_TEAM_TAG;
                          return (
                            <span key={ti} className="hearing-track-tag" style={{ background: s.bg, color: s.text }}>{label}</span>
                          );
                        })}
                      </span>
                    )}
                  </li>
                );
              })
          ) : hearing.items.map((item, i) => {
            const isTeamTracked = item.bill_id && teamTrackedBills?.has(item.bill_id);
            const isPersonallyTracked = item.bill_id && trackedBills?.has(item.bill_id);
            const teamLabels = isTeamTracked ? teamTrackedBills.get(item.bill_id) : [];
            return (
              <li key={i} className="hearing-modal-item">
                <span className="hearing-modal-item-main">
                  {item.bill_id ? (
                    <Link to={`/legislation/bill/${item.bill_id}`} className="hearing-agenda-code hearing-agenda-link" onClick={onClose}>
                      {item.item_code}
                    </Link>
                  ) : (
                    item.item_code && <span className="hearing-agenda-code">{item.item_code}</span>
                  )}
                  <span className="hearing-modal-item-desc">{item.description}</span>
                  {item.url && (
                    <a className="hearing-agenda-link ms-2" href={item.url} target="_blank" rel="noopener noreferrer">
                      Agenda <i className="bi bi-box-arrow-up-right"></i>
                    </a>
                  )}
                </span>
                {(isPersonallyTracked || isTeamTracked) && (
                  <span className="hearing-track-tags">
                    {isPersonallyTracked && (
                      <span className="hearing-track-tag hearing-track-personal">Personal</span>
                    )}
                    {teamLabels.map(({ label, color }, ti) => {
                      const s = TAG_COLORS[color] || DEFAULT_TEAM_TAG;
                      return (
                        <span key={ti} className="hearing-track-tag" style={{ background: s.bg, color: s.text }}>{label}</span>
                      );
                    })}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function TodaysHearings({ data, loading, selectedDate, onDateChange, onSetDate, trackedBills, teamTrackedBills }) {
  const [selectedHearing, setSelectedHearing] = useState(null);
  const [category, setCategory] = useState('legislature');
  const isToday = new Date().toLocaleDateString('en-CA') === selectedDate.toLocaleDateString('en-CA');
  const categoryCounts = { legislature: 0, agency: 0 };
  data.forEach((h) => { categoryCounts[h.category === 'agency' ? 'agency' : 'legislature'] += 1; });
  const visible = data.filter((h) => (h.category === 'agency' ? 'agency' : 'legislature') === category);

  // Format for the input value (YYYY-MM-DD)
  const inputDateVal = selectedDate.toLocaleDateString('en-CA', { timeZone: "America/Los_Angeles" });

  const handleTodayClick = () => {
    onSetDate(new Date());
  };

  const handleDateInput = (e) => {
    if (e.target.value) {
      const [year, month, day] = e.target.value.split('-').map(Number);
      onSetDate(new Date(year, month - 1, day));
    }
  };

  return (
    <div className="dashboard-card dashboard-card--row-height h-100">
      <div className="dashboard-card-header d-flex  justify-content-between flex-wrap gap-1">
        <div className="d-flex  align-items-center gap-2">
          <h6 className="mb-0">{isToday ? "Today's Hearings" : `Hearings`}</h6>
          {!isToday && (
             <button className="btn btn-outline-primary btn-sm py-0 px-2 rounded-pill fw-bold" style={{ fontSize: '0.68rem', height: '20px' }} onClick={handleTodayClick}>TODAY</button>
          )}
        </div>
        <div className="d-flex align-items-center gap-1">
          <div className="d-flex align-items-center gap-1">
            {!isToday && <small className="text-muted me-1" style={{ fontSize: '0.75rem' }}>{selectedDate.toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric" })}</small>}
            <button className="btn btn-sm py-0 px-1 text-muted" title="Previous Day" onClick={() => onDateChange(-1)}>
              <i className="bi bi-chevron-left"></i>
            </button>
            <div className="position-relative d-flex align-items-center">
              <i className="bi bi-calendar3" style={{ cursor: 'pointer' }}></i>
              <input
                type="date"
                className="position-absolute top-0 start-0 opacity-0 w-100 h-100"
                style={{ cursor: 'pointer' }}
                value={inputDateVal}
                onChange={handleDateInput}
                title="Jump to specific date"
              />
            </div>
            <button className="btn btn-sm py-0 px-1 text-muted" title="Next Day" onClick={() => onDateChange(1)}>
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
          <div className="tracked-bills-sort-toggle ms-2">
            {[['legislature', 'Legislature'], ['agency', 'Agencies']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`tracked-bills-sort-btn hearing-category-btn hearing-category-btn--${key}${category === key ? ' tracked-bills-sort-btn--active' : ''}`}
                onClick={() => setCategory(key)}
              >
                {label} ({loading ? '–' : categoryCounts[key]})
              </button>
            ))}
          </div>
        </div>
      </div>
      {loading ? (
        <div className="p-3"><SkeletonRows count={6} /></div>
      ) : visible.length === 0 ? (
        <div className="dashboard-empty">
          {category === 'agency'
            ? `No agency board or commission meetings listed for ${isToday ? 'today' : 'this day'}.`
            : `No legislative hearings scheduled for ${isToday ? 'today' : 'this day'}.`}
        </div>
      ) : (
        <ul className="hearings-list">
          {visible.map((h) => {
            const postponed = h.status === 'Postponed' || h.status === 'Cancelled';

            // Derive chamber for leginfo rows from the committee name
            const name = (h.name || '').toLowerCase();
            const isAgency = h.category === 'agency';
            const chamber = isAgency ? null : h.chamber
              || (name.includes('assembly') || name.startsWith('asm') ? 'Assembly' : null)
              || (name.includes('senate') || name.startsWith('sen')    ? 'Senate'   : null);

            const location = h.location || h.loc_line_1 || "";

            const sessions = isAgency && h.sessions ? h.sessions : [];
            const hasItems = !isAgency && h.items && h.items.length > 0;
            const agendaUrl = isAgency && h.url && sessions.length === 0;

            return (
              <li key={h.id} className={`hearing-item${postponed ? ' hearing-postponed' : ''}${chamber ? ` hearing-item-${chamber.toLowerCase()}` : ''}`}>
                <div className="hearing-left">
                  <div className="hearing-committee">
                    {hearingTitle(h)}
                    {postponed && (
                      <span className="hearing-status-badge">{h.status}</span>
                    )}
                  </div>
                  {(h.chair || h.agency) && <div className="hearing-chair">{h.chair || h.agency}</div>}
                  {location && <div className="hearing-location">{location}</div>}
                  {sessions.length > 0 && (
                    <div className="hearing-sessions">
                      {sessions.length} meetings: {sessions.map((x) => x.name.replace(/\s+Meeting$/i, '')).join(' · ')}
                    </div>
                  )}
                  {h.note && !postponed && <div className="hearing-note">{h.note}</div>}
                </div>
                <div className="hearing-right">
                  <div className="hearing-time">{formatTime(h.agenda_time) || (isAgency ? 'Time TBA' : '')}</div>
                  <div className="hearing-actions-row">
                    {agendaUrl && (
                      <a
                        className="hearing-agenda-toggle"
                        href={h.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Agenda <i className="bi bi-box-arrow-up-right"></i>
                      </a>
                    )}
                    {sessions.length > 0 && (
                      <span
                        className="hearing-agenda-toggle"
                        onClick={() => setSelectedHearing(h)}
                      >
                        Details <i className="bi bi-view-list"></i>
                      </span>
                    )}
                    {hasItems && (
                      <span
                        className="hearing-agenda-toggle"
                        onClick={() => setSelectedHearing(h)}
                      >
                        Agenda <i className="bi bi-view-list"></i>
                      </span>
                    )}
                    <CalendarAddButton
                      hearing={h}
                      hearingDate={selectedDate}
                      trackedBills={trackedBills}
                      teamTrackedBills={teamTrackedBills}
                    />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <HearingAgendaModal
        hearing={selectedHearing}
        hearingDate={selectedDate}
        onClose={() => setSelectedHearing(null)}
        trackedBills={trackedBills}
        teamTrackedBills={teamTrackedBills}
      />
    </div>
  );
}

function PostModal({ post, onClose }) {
  const { auth } = useContext(AuthContext);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [likedPost, setLikedPost] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [commentInput, setCommentInput] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    if (!post) return;
    setDetail(null);
    setCommentInput("");
    setLikeCount(Number(post.like_count) || 0);
    setLikedPost(false);
    setLoadingDetail(true);

    const fetchDetail = API.getSinglePost(post.post_id);
    const fetchLikes = auth.user
      ? API.getUserLikes(auth.user.user_id)
      : Promise.resolve({ data: [] });

    Promise.all([fetchDetail, fetchLikes])
      .then(([detailRes, likesRes]) => {
        setDetail(detailRes.data);
        setLikeCount(detailRes.data.likes_count || 0);
        if (auth.user) {
          setLikedPost(
            (likesRes.data || []).some((l) => l.post_id === post.post_id && l.liked === true)
          );
        }
      })
      .catch((err) => console.error("PostModal fetch error:", err))
      .finally(() => setLoadingDetail(false));
  }, [post?.post_id]);

  const handleLike = async () => {
    if (!auth.user || !post) return;
    const newLiked = !likedPost;
    const newCount = newLiked ? likeCount + 1 : likeCount - 1;
    setLikedPost(newLiked);
    setLikeCount(newCount);
    try {
      await API.addNewLike({ post_id: post.post_id, likes_count: newCount, liked: newLiked, user_id: auth.user.user_id });
    } catch (err) {
      console.error("Like failed:", err);
      setLikedPost(!newLiked);
      setLikeCount(likeCount);
    }
  };

  const handleComment = async () => {
    if (!auth.user || !commentInput.trim() || !post) return;
    setSubmittingComment(true);
    try {
      await API.addNewComment({
        post_id: post.post_id,
        comment_author: auth.user.profile_id,
        comment_body: commentInput.trim(),
        user_id: auth.user.user_id,
      });
      setCommentInput("");
      const res = await API.getSinglePost(post.post_id);
      setDetail(res.data);
    } catch (err) {
      console.error("Comment failed:", err);
    } finally {
      setSubmittingComment(false);
    }
  };

  if (!post) return null;
  const authorName = [post.first_name, post.last_name].filter(Boolean).join(" ");
  const dateStr = post.created_at
    ? new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;
  const comments = detail?.PostComments ?? [];
  const sanitizedBody = detail?.post_body ? DOMPurify.sanitize(detail.post_body) : null;

  return (
    <div className="hearing-modal-overlay" onClick={onClose}>
      <div className="post-modal" onClick={(e) => e.stopPropagation()}>
        <div className="post-modal-header">
          <div className="hearing-modal-title">{post.post_title}</div>
          <button className="hearing-modal-close" onClick={onClose}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        <div className="hearing-modal-meta">
          {authorName && <span><i className="bi bi-person me-1"></i>{authorName}</span>}
          {dateStr && <span><i className="bi bi-calendar3 me-1"></i>{dateStr}</span>}
        </div>
        <div className="post-modal-body">
          {loadingDetail ? (
            <SkeletonRows count={4} />
          ) : sanitizedBody ? (
            <div dangerouslySetInnerHTML={{ __html: sanitizedBody }} />
          ) : (
            <p style={{ whiteSpace: "pre-wrap" }}>{post.post_body}</p>
          )}
        </div>
        <div className="post-modal-actions">
          <button
            className={`post-modal-like-btn${likedPost ? " liked" : ""}`}
            onClick={handleLike}
            disabled={!auth.user}
            title={!auth.user ? "Sign in to like" : ""}
          >
            <i className={`bi ${likedPost ? "bi-heart-fill" : "bi-heart"}`}></i>
            <span>{likeCount}</span>
          </button>
        </div>
        {!loadingDetail && comments.length > 0 && (
          <ul className="post-modal-comment-list">
            {comments.map((c) => {
              const cName = [c.ProfileInfo?.first_name, c.ProfileInfo?.last_name].filter(Boolean).join(" ");
              const cDate = c.created_at
                ? new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                : null;
              return (
                <li key={c.comment_id} className="post-modal-comment">
                  <div className="post-modal-comment-meta">
                    <span className="post-modal-comment-author">{cName || "Anonymous"}</span>
                    {cDate && <span className="post-modal-comment-date">{cDate}</span>}
                  </div>
                  <div className="post-modal-comment-body">{c.comment_body}</div>
                </li>
              );
            })}
          </ul>
        )}
        {auth.user && (
          <div className="post-modal-comment-input">
            <textarea
              className="form-control post-modal-comment-textarea"
              placeholder="Add a comment…"
              value={commentInput}
              onChange={(e) => setCommentInput(e.target.value)}
              rows={2}
            />
            <button
              className="btn post-modal-comment-submit"
              onClick={handleComment}
              disabled={!commentInput.trim() || submittingComment}
            >
              {submittingComment ? "Posting…" : "Post"}
            </button>
          </div>
        )}
        <div className="post-modal-footer">
          <Link to="/public-square" className="post-modal-ps-link" onClick={onClose}>
            View all on Public Square <i className="bi bi-arrow-right"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}

function TopPosts({ data, loading }) {
  const [selectedPost, setSelectedPost] = useState(null);
  return (
    <div className="dashboard-card dashboard-card--scrollable-list dashboard-card--row-height h-100">
      <div className="dashboard-card-header">
        <i className="bi bi-chat-heart-fill"></i>
        <h6>Top Posts</h6>
      </div>
      {loading ? (
        <div className="p-3"><SkeletonRows count={6} /></div>
      ) : data.length === 0 ? (
        <div className="dashboard-empty">No posts available.</div>
      ) : (
        <>
          <ul className="top-headlines-list">
            {data.map((p, i) => (
              <li key={p.post_id}>
                <button className="top-headline-item top-post-item" onClick={() => setSelectedPost(p)}>
                  <span className="headline-rank">{i + 1}</span>
                  <div className="headline-body">
                    {(p.first_name || p.last_name) && (
                      <div className="headline-pub">{[p.first_name, p.last_name].filter(Boolean).join(" ")}</div>
                    )}
                    <div className="headline-ttl">{p.post_title}</div>
                  </div>
                  {Number(p.like_count) > 0 && (
                    <div className="headline-clicks">
                      <i className="bi bi-heart-fill"></i>
                      {p.like_count}
                    </div>
                  )}
                </button>
              </li>
            ))}
          </ul>
          <div className="top-posts-footer">
            <Link to="/public-square" className="top-posts-more-link">
              All posts on Public Square <i className="bi bi-arrow-right"></i>
            </Link>
          </div>
        </>
      )}
      <PostModal post={selectedPost} onClose={() => setSelectedPost(null)} />
    </div>
  );
}

const PRIORITY_RANK = { Urgent: 1, High: 2, Medium: 3, Low: 4 };
const PRIORITY_CLASS = { Urgent: "task-priority-urgent", High: "task-priority-high", Medium: "task-priority-medium", Low: "task-priority-low" };

const TODAY_PT = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

function WorkspaceWidget({ data, loading, userId, onTaskCreated, onTaskEdited }) {
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackClients, setFeedbackClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [pendingTeamSelection, setPendingTeamSelection] = useState(false);
  const [feedbackModalTeam, setFeedbackModalTeam] = useState(null);

  if (loading) {
    return (
      <div className="dashboard-card dashboard-card--fixed">
        <div className="dashboard-card-header"><i className="bi bi-briefcase-fill"></i><h6>Workspace</h6></div>
        <div className="p-3"><SkeletonRows count={5} /></div>
      </div>
    );
  }
  if (!data) return null;

  const { teams, tasks } = data;
  const multiTeam = teams.length > 1;
  const selectedTeam = selectedTeamId ? teams.find((t) => t.team_id === selectedTeamId) : null;

  // For single-team, feedbackTeam is always set; for multi, requires a specific selection
  const feedbackTeam = selectedTeam ?? (teams.length === 1 ? teams[0] : null);
  const isClientType = CLIENT_LABEL_TYPES.includes(feedbackTeam?.group_type);
  const feedbackLabel = feedbackTeam
    ? (isClientType ? "Client Feedback" : "Stakeholder Feedback")
    : "Workspace Feedback";

  const filteredTasks = selectedTeamId
    ? tasks.filter((t) => t.team_id === selectedTeamId)
    : tasks;

  const sorted = [...filteredTasks].sort((a, b) => {
    const aNull = a.due_date == null, bNull = b.due_date == null;
    if (aNull !== bNull) return aNull ? 1 : -1;
    if (a.due_date !== b.due_date) return a.due_date < b.due_date ? -1 : 1;
    return (PRIORITY_RANK[a.priority] ?? 5) - (PRIORITY_RANK[b.priority] ?? 5);
  });

  const hasTasks = sorted.length > 0;

  const openFeedbackForTeam = async (team) => {
    setLoadingClients(true);
    try {
      const res = await API.getTeamClients(team.team_id);
      setFeedbackClients(res.data.filter((c) => !c.archived));
      setFeedbackModalTeam(team);
      setShowFeedbackModal(true);
    } catch (err) {
      console.error("Error loading clients for feedback modal:", err);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleFeedbackClick = () => {
    if (!teams.length) return;
    if (feedbackTeam) {
      openFeedbackForTeam(feedbackTeam);
    } else {
      setPendingTeamSelection(true);
    }
  };

  return (
    <>
      <div className="dashboard-card dashboard-card--fixed">
        <div className="dashboard-card-header">
          <i className="bi bi-briefcase-fill"></i>
          {multiTeam ? (
            <div className="workspace-team-select-wrap">
              <select
                className="workspace-team-select"
                value={selectedTeamId ?? ""}
                onChange={(e) => setSelectedTeamId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">All Workspaces</option>
                {teams.map((t) => (
                  <option key={t.team_id} value={t.team_id}>{t.team_name}</option>
                ))}
              </select>
              <i className="bi bi-chevron-down workspace-team-select-caret"></i>
            </div>
          ) : (
            <h6>Workspace</h6>
          )}
          <button
            className="btn workspace-feedback-header-btn ms-auto"
            onClick={handleFeedbackClick}
            disabled={!teams.length || loadingClients}
          >
            <i className="bi bi-chat-square-text"></i>
            {loadingClients ? "Loading…" : feedbackLabel}
          </button>
        </div>
        <div className="workspace-widget-body">
            <div className="workspace-tasks-section">
              <div className="workspace-tasks-header">
                <span>Outstanding Tasks</span>
                {teams.length > 0 && (
                  <button type="button" className="workspace-add-task-btn" onClick={() => setShowAddTask(true)}>+ Add Task</button>
                )}
              </div>
              {!hasTasks ? (
                <div className="dashboard-empty">You have no outstanding tasks.</div>
              ) : (
                <ul className="workspace-tasks-list">
                  {sorted.map((task) => {
                    const overdue = task.due_date && task.due_date < TODAY_PT;
                    return (
                    <li
                      key={task.id}
                      className={`workspace-task-item workspace-task-item--clickable${overdue ? " workspace-task-item--overdue" : ""}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetailTask(task)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailTask(task); } }}
                    >
                      <div className="workspace-task-main">
                        <span className="workspace-task-team">
                          {task.TeamAccount?.team_name}
                          {task.Bill && (
                            <Link
                              to={`/legislation/bill/${task.Bill.bill_id}`}
                              className="workspace-task-bill"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {task.Bill.bill_type} {task.Bill.bill_number}
                            </Link>
                          )}
                          {task.rulemaking_ref && (
                            task.rulemaking_url ? (
                              <a href={task.rulemaking_url} target="_blank" rel="noopener noreferrer" className="workspace-task-bill" title={task.rulemaking_title || undefined} onClick={(e) => e.stopPropagation()}>
                                Rulemaking
                              </a>
                            ) : (
                              <span className="workspace-task-bill" title={task.rulemaking_title || undefined}>Rulemaking</span>
                            )
                          )}
                        </span>
                        <span className="workspace-task-text">{task.task_text}</span>
                      </div>
                      <div className="workspace-task-meta">
                        {task.due_date && (
                          <span className={`workspace-task-due${overdue ? " workspace-task-due--overdue" : ""}`}>
                            {overdue && <i className="bi bi-exclamation-circle-fill me-1"></i>}
                            {new Date(task.due_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </span>
                        )}
                        {task.priority && (
                          <span className={`workspace-task-priority ${PRIORITY_CLASS[task.priority] || ""}`}>
                            {task.priority}
                          </span>
                        )}
                      </div>
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
      </div>
      {detailTask && (
        <TaskDetailModal
          task={detailTask}
          userId={userId}
          onClose={() => setDetailTask(null)}
          onUpdated={(fields) => {
            setDetailTask((prev) => prev && { ...prev, ...fields });
            onTaskEdited(detailTask.id, fields);
          }}
        />
      )}
      {showAddTask && (
        <AddTaskModal
          teams={teams}
          defaultTeamId={selectedTeamId ?? teams[0].team_id}
          userId={userId}
          onCreated={onTaskCreated}
          onClose={() => setShowAddTask(false)}
        />
      )}
      {pendingTeamSelection && (
        <div className="hearing-modal-overlay" onClick={() => setPendingTeamSelection(false)}>
          <div className="workspace-select-modal" onClick={(e) => e.stopPropagation()}>
            <div className="workspace-select-modal-header">
              <span>Select Workspace</span>
            </div>
            <div className="workspace-select-modal-body">
              {teams.map((t) => (
                <button
                  key={t.team_id}
                  className="workspace-select-team-btn"
                  onClick={() => { setPendingTeamSelection(false); openFeedbackForTeam(t); }}
                >
                  {t.org_logo && (
                    <img src={t.org_logo} alt="" className="workspace-select-team-logo" />
                  )}
                  <span className="workspace-select-team-name">{t.team_name}</span>
                </button>
              ))}
            </div>
            <div className="workspace-select-modal-footer">
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setPendingTeamSelection(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showFeedbackModal && feedbackModalTeam && (
        <CreateFeedbackRequestModal
          teamId={feedbackModalTeam.team_id}
          clients={feedbackClients}
          isStakeholderGroup={!CLIENT_LABEL_TYPES.includes(feedbackModalTeam.group_type)}
          onClose={() => { setShowFeedbackModal(false); setFeedbackModalTeam(null); }}
          onCreated={() => { setShowFeedbackModal(false); setFeedbackModalTeam(null); }}
        />
      )}
    </>
  );
}

function SubscriptionPromo({ variant }) {
  const isFree = variant !== "individual";
  return (
    <div className="dashboard-card subscription-promo-card h-100">
      <div className="subscription-promo-body">
        <div className="subscription-promo-icon">
          <i className={`bi ${isFree ? "bi-star-fill" : "bi-people-fill"}`}></i>
        </div>
        <h6 className="subscription-promo-title">
          {isFree ? "Unlock Full Access" : "Add Team Access"}
        </h6>
        <p className="subscription-promo-desc">
          {isFree
            ? "Subscribe to track California bills, follow real-time hearings, and coordinate with your team."
            : "Take your subscription further — collaborate with a team, manage client feedback, and track legislation together."}
        </p>
        <ul className="subscription-promo-list">
          {isFree ? (
            <>
              <li><i className="bi bi-check2-circle"></i> Bill tracking &amp; alerts</li>
              <li><i className="bi bi-check2-circle"></i> Full legislative history</li>
              <li><i className="bi bi-check2-circle"></i> Workspace &amp; team tools</li>
            </>
          ) : (
            <>
              <li><i className="bi bi-check2-circle"></i> Team bill tracking</li>
              <li><i className="bi bi-check2-circle"></i> Client &amp; stakeholder feedback</li>
              <li><i className="bi bi-check2-circle"></i> Shared tasks &amp; workspace</li>
            </>
          )}
        </ul>
        <Link to="/plans" className="btn subscription-promo-btn">
          {isFree ? "View Plans" : "Upgrade"}
        </Link>
      </div>
    </div>
  );
}

function TrackedBillsStatus({ data, loading }) {
  const [sortMode, setSortMode] = useState('recent');
  const [statusFilter, setStatusFilter] = useState(new Set());
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleStatusFilter = (key) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const infoByBillId = new Map(data.map((bill) => [bill.bill_id, trackedBillStatusInfo(bill)]));
  const filtered = statusFilter.size === 0
    ? data
    : data.filter((bill) => statusFilter.has(infoByBillId.get(bill.bill_id).category));
  const sortedBills = sortTrackedBills(filtered, sortMode);

  return (
    <div className="dashboard-card tracked-bills-card dashboard-card--scrollable-list dashboard-card--row-height h-100">
      <div className="dashboard-card-header justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-binoculars-fill"></i>
          <h6 className="mb-0">Tracked Bills</h6>
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="tracked-bills-sort-toggle">
            {TRACKED_BILL_SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`tracked-bills-sort-btn${sortMode === opt.key ? ' tracked-bills-sort-btn--active' : ''}`}
                onClick={() => setSortMode(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="tracked-bills-filter" ref={filterRef}>
            <button
              type="button"
              className={`tracked-bills-filter-toggle${statusFilter.size > 0 ? ' tracked-bills-filter-toggle--active' : ''}`}
              onClick={() => setFilterOpen((v) => !v)}
            >
              <i className="bi bi-funnel"></i>
              Status{statusFilter.size > 0 ? ` (${statusFilter.size})` : ''}
            </button>
            {filterOpen && (
              <div className="tracked-bills-filter-panel">
                {TRACKED_BILL_STATUS_OPTIONS.map((opt) => (
                  <label key={opt.key} className="tracked-bills-filter-item form-check mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={statusFilter.has(opt.key)}
                      onChange={() => toggleStatusFilter(opt.key)}
                    />
                    <span className="form-check-label">{opt.label}</span>
                  </label>
                ))}
                {statusFilter.size > 0 && (
                  <button type="button" className="tracked-bills-filter-clear" onClick={() => setStatusFilter(new Set())}>
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {loading ? (
        <div className="p-3"><SkeletonRows count={8} /></div>
      ) : data.length === 0 ? (
        <div className="dashboard-empty">No bills being tracked yet.</div>
      ) : sortedBills.length === 0 ? (
        <div className="dashboard-empty">No tracked bills match the selected status filter.</div>
      ) : (
        <ul className="tracked-bills-list">
          {sortedBills.map((bill) => {
            const { billDisplay } = deriveBillDisplayFields(bill);
            const statusInfo = infoByBillId.get(bill.bill_id);
            return (
              <li key={bill.bill_id} className="tracked-bill-item">
                <div className="tracked-bill-main">
                  <Link to={`/legislation/bill/${bill.bill_id}`} className="tracked-bill-number">
                    {billDisplay}
                  </Link>
                  <span className="tracked-bill-title">{bill.title}</span>
                </div>
                <span className={`tracked-bill-status ${statusInfo.className}`} title={statusInfo.text}>
                  {statusInfo.text}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function TopHeadlines({ data, loading }) {
  const handleClick = (id) => {
    if (id) API.trackHeadlineClick(id).catch(() => {});
  };

  return (
    <div className="dashboard-card dashboard-card--scrollable-list dashboard-card--row-height h-100">
      <div className="dashboard-card-header">
        <i className="bi bi-fire"></i>
        <h6>Top Headlines</h6>
      </div>
      {loading ? (
        <div className="p-3"><SkeletonRows count={8} /></div>
      ) : data.length === 0 ? (
        <div className="dashboard-empty">No headline data yet.</div>
      ) : (
        <ul className="top-headlines-list">
          {data.map((h, i) => (
            <li key={h.headline_id}>
              <a
                href={h.url}
                target="_blank"
                rel="noreferrer"
                className="top-headline-item"
                onClick={() => handleClick(h.headline_id)}
              >
                <span className="headline-rank">{i + 1}</span>
                <div className="headline-body">
                  <div className="headline-pub">{h.publication}</div>
                  <div className="headline-ttl">{h.title}</div>
                </div>
                {h.click_count > 0 && (
                  <div className="headline-clicks">
                    <i className="bi bi-cursor-fill"></i>
                    {h.click_count}
                  </div>
                )}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Dates arrive as bare YYYY-MM-DD strings; parsing them as UTC keeps them from sliding a day.
function parseRulemakingDate(value) {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function formatRulemakingDate(value) {
  return parseRulemakingDate(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function daysUntilLabel(value) {
  const days = Math.round((parseRulemakingDate(value) - parseRulemakingDate(TODAY_PT_DATE)) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
}

// "GOV": ["12945", "12935"] -> ["GOV §12945", "GOV §12935"]
function flattenStatutes(statutes) {
  return Object.entries(statutes || {}).flatMap(([code, sections]) => sections.map((s) => `${code} §${s}`));
}

const STATUTE_CHIP_LIMIT = 8;

function RulemakingCalendarLinks({ action }) {
  const milestones = [
    action.hearing_date && {
      label: 'Hearing',
      event: buildRulemakingEvent({
        title: `Public hearing: ${action.subject} (${action.agency})`,
        location: action.hearing_location,
        description: `${action.agency}\n${action.subject}${action.source_url ? `\n\n${action.source_url}` : ''}`,
        date: action.hearing_date,
        timeStr: action.hearing_time,
      }),
    },
    action.comment_deadline && {
      label: 'Comment deadline',
      event: buildRulemakingEvent({
        title: `Comments due: ${action.subject} (${action.agency})`,
        description: `Written comments on a proposed regulation are due.\n${action.agency}\n${action.subject}${action.source_url ? `\n\n${action.source_url}` : ''}`,
        date: action.comment_deadline,
      }),
    },
  ].filter(Boolean);

  return milestones.map(({ label, event }) => (
    <div key={label} className="rulemaking-cal">
      <span className="rulemaking-cal-label"><i className="bi bi-calendar-plus"></i>{label}</span>
      <button type="button" onClick={() => window.open(googleCalendarUrl(event), '_blank', 'noopener,noreferrer')}>Google</button>
      <button type="button" onClick={() => downloadICS(event, event.title)}>Apple / Outlook</button>
    </div>
  ));
}

function UpcomingRulemaking({ data, loading, workspace, userId, onTaskCreated, onTaskUpdated, onTaskDeleted }) {
  const [agency, setAgency] = useState('');
  const [openIds, setOpenIds] = useState(new Set());

  const agencies = [...new Set(data.map((a) => a.agency))].sort((a, b) => a.localeCompare(b));
  // Fall back to "all" if the selected agency has no upcoming actions left in the data.
  const selected = agencies.includes(agency) ? agency : '';
  const visible = selected ? data.filter((a) => a.agency === selected) : data;

  const toggle = (id) => setOpenIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="dashboard-card rulemaking-card dashboard-card--scrollable-list dashboard-card--row-height h-100">
      <div className="dashboard-card-header">
        <i className="bi bi-megaphone-fill"></i>
        <h6>Upcoming Rulemaking</h6>
        {!loading && data.length > 0 && (
          <span className="rulemaking-count">{visible.length}</span>
        )}
        <div className="rulemaking-agency-select-wrap ms-auto">
          <select
            className="rulemaking-agency-select"
            value={selected}
            onChange={(e) => setAgency(e.target.value)}
            disabled={loading || agencies.length === 0}
            aria-label="Filter by agency, board, or commission"
          >
            <option value="">All Agencies</option>
            {agencies.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <i className="bi bi-chevron-down rulemaking-agency-select-caret"></i>
        </div>
      </div>
      {loading ? (
        <div className="p-3"><SkeletonRows count={6} /></div>
      ) : data.length === 0 ? (
        <div className="dashboard-empty">No upcoming rulemaking hearings or comment deadlines.</div>
      ) : (
        <ul className="rulemaking-list">
          {visible.map((action) => {
            const date = parseRulemakingDate(action.date);
            const isHearing = action.kind === 'hearing';
            const urgent = action.date === TODAY_PT_DATE;
            const isOpen = openIds.has(action.id);
            const taskCount = (workspace?.rulemaking_tasks || []).filter((t) => t.rulemaking_ref === action.ref && !t.completed).length;
            const videoLinks = action.hearing_date && action.video_links ? action.video_links : [];
            const where = [action.hearing_time, action.hearing_location].filter(Boolean).join(' · ');
            // The other milestone on the same notice, when there is one.
            const alsoCommentDeadline = isHearing && action.comment_deadline && action.comment_deadline !== action.date;
            const alsoHearing = !isHearing && action.hearing_date;
            // With no hearing set, the public can still ask for one before the comment deadline.
            const noHearing = !isHearing && !action.hearing_date && action.hearing_status === 'none';
            const statutes = flattenStatutes(action.statutes);
            const hasMilestone = action.hearing_date || action.comment_deadline;
            return (
              <li key={action.id} className={`rulemaking-item${isOpen ? ' rulemaking-item--open' : ''}`}>
                <div
                  className="rulemaking-row"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={() => toggle(action.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(action.id); } }}
                >
                  <div className={`rulemaking-date${urgent ? ' rulemaking-date--today' : ''}`}>
                    <span className="rulemaking-date-month">{date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })}</span>
                    <span className="rulemaking-date-day">{date.getUTCDate()}</span>
                  </div>
                  <div className="rulemaking-main">
                    <div className="rulemaking-agency">
                      <span className="rulemaking-agency-name">{action.agency}</span>
                      {action.title_number && <span className="rulemaking-title-chip">CCR Title {action.title_number}</span>}
                    </div>
                    <span className={`rulemaking-subject${isOpen ? ' rulemaking-subject--open' : ''}`}>{action.subject}</span>
                    {(where || videoLinks.length > 0 || taskCount > 0 || alsoCommentDeadline || alsoHearing || noHearing) && (
                      <div className="rulemaking-meta">
                        {where && <span><i className="bi bi-geo-alt"></i>{where}</span>}
                        {videoLinks.length > 0 && <span><i className="bi bi-camera-video"></i>Join online</span>}
                        {taskCount > 0 && <span><i className="bi bi-check2-square"></i>{taskCount} {taskCount === 1 ? "task" : "tasks"}</span>}
                        {alsoCommentDeadline && <span><i className="bi bi-chat-left-text"></i>Comments due {formatRulemakingDate(action.comment_deadline)}</span>}
                        {alsoHearing && <span><i className="bi bi-people"></i>Hearing {formatRulemakingDate(action.hearing_date)}</span>}
                        {noHearing && <span className="rulemaking-meta--muted"><i className="bi bi-people"></i>No hearing scheduled; one can be requested</span>}
                      </div>
                    )}
                  </div>
                  <div className="rulemaking-side">
                    <span className={`rulemaking-kind ${isHearing ? 'rulemaking-kind--hearing' : 'rulemaking-kind--comment'}`}>
                      {isHearing ? 'Hearing' : 'Comments Due'}
                    </span>
                    <span className={`rulemaking-countdown${urgent ? ' rulemaking-countdown--today' : ''}`}>{daysUntilLabel(action.date)}</span>
                  </div>
                  <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} rulemaking-chevron`}></i>
                </div>
                {isOpen && (
                  <div className="rulemaking-detail">
                    <div className="rulemaking-facts">
                      {action.notice_file_number && <span><strong>Notice</strong>{action.notice_file_number}</span>}
                      {action.register_date && <span><strong>Published</strong>{formatRulemakingDate(action.register_date)}</span>}
                      {action.hearing_date && <span><strong>Hearing</strong>{formatRulemakingDate(action.hearing_date)}</span>}
                      {action.comment_deadline && <span><strong>Comments due</strong>{formatRulemakingDate(action.comment_deadline)}</span>}
                    </div>
                    {action.details && <p className="rulemaking-detail-text">{action.details}</p>}
                    {statutes.length > 0 && (
                      <div className="rulemaking-chip-group">
                        <span className="rulemaking-chip-label">Statutes cited</span>
                        {statutes.slice(0, STATUTE_CHIP_LIMIT).map((s) => <span key={s} className="rulemaking-chip">{s}</span>)}
                        {statutes.length > STATUTE_CHIP_LIMIT && (
                          <span className="rulemaking-chip rulemaking-chip--more">+{statutes.length - STATUTE_CHIP_LIMIT} more</span>
                        )}
                      </div>
                    )}
                    {action.bills?.length > 0 && (
                      <div className="rulemaking-chip-group">
                        <span className="rulemaking-chip-label">Names</span>
                        {action.bills.map((b) => <span key={b} className="rulemaking-chip rulemaking-chip--bill">{b}</span>)}
                      </div>
                    )}
                    <div className="rulemaking-actions">
                      {videoLinks.map((link) => (
                        <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="rulemaking-video-link">
                          <i className={`bi ${link.label === 'Livestream' ? 'bi-broadcast' : 'bi-camera-video'}`}></i>
                          {link.label === 'Livestream' ? 'Watch livestream' : `Join on ${link.label}`}
                        </a>
                      ))}
                      {action.source_url && (
                        <a href={action.source_url} target="_blank" rel="noopener noreferrer" className="rulemaking-open-link">
                          {action.notice_file_number ? 'Open notice' : 'Open hearing page'} <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                      )}
                      {hasMilestone && <RulemakingCalendarLinks action={action} />}
                    </div>
                    {workspace?.teams?.length > 0 && (
                      <RulemakingTasks
                        item={{ ref: action.ref, title: `${action.agency}: ${action.subject}`, url: action.source_url, dueDate: action.date }}
                        teams={workspace.teams}
                        tasks={workspace.rulemaking_tasks || []}
                        userId={userId}
                        onCreated={onTaskCreated}
                        onUpdated={onTaskUpdated}
                        onDeleted={onTaskDeleted}
                      />
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { auth, teamsAccess, workplaceSubscriber, individualSubscription, accessLoading } = useContext(AuthContext);
  const [counts, setCounts] = useState([]);
  const [hearings, setHearings] = useState([]);
  const [headlines, setHeadlines] = useState([]);
  const [tracking, setTracking] = useState(null);
  const [trackedBills, setTrackedBills] = useState(new Set());
  const [teamTrackedBills, setTeamTrackedBills] = useState(new Map());
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [loadingHearings, setLoadingHearings] = useState(true);
  const [loadingHeadlines, setLoadingHeadlines] = useState(true);
  const [topPosts, setTopPosts] = useState([]);
  const [loadingTopPosts, setLoadingTopPosts] = useState(true);
  const [rulemaking, setRulemaking] = useState([]);
  const [loadingRulemaking, setLoadingRulemaking] = useState(true);
  const [workspaceData, setWorkspaceData] = useState(null);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [trackedBillsStatus, setTrackedBillsStatus] = useState([]);
  const [loadingTrackedBillsStatus, setLoadingTrackedBillsStatus] = useState(true);

  const [hearingDate, setHearingDate] = useState(new Date());

  const isWorkspaceMember = teamsAccess || workplaceSubscriber;
  const showPromo = !accessLoading && !isWorkspaceMember;
  const promoVariant = individualSubscription ? "individual" : "free";

  // Static Dashboard Data
  useEffect(() => {
    API.getDashboardCounts()
      .then((res) => setCounts(res.data))
      .catch((err) => console.error("Counts fetch error:", err))
      .finally(() => setLoadingCounts(false));

    API.getDashboardHeadlines()
      .then((res) => setHeadlines(res.data))
      .catch((err) => console.error("Headlines fetch error:", err))
      .finally(() => setLoadingHeadlines(false));

    API.getDashboardTopPosts()
      .then((res) => setTopPosts(res.data))
      .catch(() => {})
      .finally(() => setLoadingTopPosts(false));

    API.getDashboardRulemaking()
      .then((res) => setRulemaking(res.data))
      .catch((err) => console.error("Rulemaking fetch error:", err))
      .finally(() => setLoadingRulemaking(false));
  }, []);

  // Tracking counts + bill tracking sets (depends on auth)
  useEffect(() => {
    const userId = auth?.user?.user_id;
    if (!userId) {
      setTrackedBills(new Set());
      setTeamTrackedBills(new Map());
      setTrackedBillsStatus([]);
      setLoadingTrackedBillsStatus(false);
      return;
    }
    API.getDashboardTracking(userId)
      .then((res) => setTracking(res.data))
      .catch(() => {});
    API.getTrackedBills(userId)
      .then((res) => setTrackedBills(new Set(res.data)))
      .catch(() => {});
    API.getTeamTrackedBills(userId)
      .then((res) => {
        const map = new Map();
        res.data.forEach(({ bill_id, team_name, team_acronym, tag_color }) => {
          if (!map.has(bill_id)) map.set(bill_id, []);
          const label = team_acronym || team_name.split(/\s+/).map(w => w[0]).join('').toUpperCase();
          map.get(bill_id).push({ label, color: tag_color || null });
        });
        setTeamTrackedBills(map);
      })
      .catch(() => {});

    setLoadingTrackedBillsStatus(true);
    API.getDashboardTrackedBills(userId)
      .then((res) => setTrackedBillsStatus(res.data))
      .catch((err) => console.error("Tracked bills status fetch error:", err))
      .finally(() => setLoadingTrackedBillsStatus(false));
  }, [auth?.user?.user_id]);

  // Workspace data (only for workspace members)
  useEffect(() => {
    const userId = auth?.user?.user_id;
    if (!userId || !isWorkspaceMember) return;
    setLoadingWorkspace(true);
    API.getDashboardWorkspace(userId)
      .then((res) => setWorkspaceData(res.data))
      .catch(() => {})
      .finally(() => setLoadingWorkspace(false));
  }, [auth?.user?.user_id, isWorkspaceMember]);

  // Keep the Workspace widget's own task list in step with tasks added to rulemaking items
  const handleRulemakingTaskCreated = (task) => {
    setWorkspaceData((prev) => {
      if (!prev) return prev;
      const teamName = prev.teams.find((t) => t.team_id === task.team_id)?.team_name;
      const mine = task.assigned_to_user_id === auth?.user?.user_id;
      return {
        ...prev,
        rulemaking_tasks: [...prev.rulemaking_tasks, task],
        tasks: mine ? [...prev.tasks, { ...task, TeamAccount: { team_name: teamName } }] : prev.tasks,
      };
    });
  };

  // A task from the Add Task modal: linked to a rulemaking item, a bill, or neither. Either way it
  // only shows in the widget's own list when it's mine.
  const handleModalTaskCreated = (task) => {
    if (task.rulemaking_ref) {
      handleRulemakingTaskCreated(task);
      return;
    }
    setWorkspaceData((prev) => {
      if (!prev || task.assigned_to_user_id !== auth?.user?.user_id) return prev;
      const teamName = prev.teams.find((t) => t.team_id === task.team_id)?.team_name;
      return { ...prev, tasks: [...prev.tasks, { ...task, TeamAccount: { team_name: teamName } }] };
    });
  };

  const handleRulemakingTaskUpdated = (task) => {
    setWorkspaceData((prev) => prev && ({
      ...prev,
      rulemaking_tasks: prev.rulemaking_tasks.map((t) => (t.id === task.id ? task : t)),
      tasks: task.completed ? prev.tasks.filter((t) => t.id !== task.id) : prev.tasks,
    }));
  };

  // Fields edited from a task's detail modal, merged in so the item keeps its Bill / TeamAccount
  const handleTaskEdited = (taskId, fields) => {
    const merge = (t) => (t.id === taskId ? { ...t, ...fields } : t);
    setWorkspaceData((prev) => prev && ({
      ...prev,
      tasks: prev.tasks.map(merge),
      rulemaking_tasks: (prev.rulemaking_tasks || []).map(merge),
    }));
  };

  const handleRulemakingTaskDeleted = (task) => {
    setWorkspaceData((prev) => prev && ({
      ...prev,
      rulemaking_tasks: prev.rulemaking_tasks.filter((t) => t.id !== task.id),
      tasks: prev.tasks.filter((t) => t.id !== task.id),
    }));
  };

  // Date-dependent Hearings Data
  useEffect(() => {
    setLoadingHearings(true);
    const dateStr = hearingDate.toLocaleDateString('en-CA', { timeZone: "America/Los_Angeles" });
    API.getDashboardHearings(dateStr)
      .then((res) => setHearings(res.data))
      .catch((err) => console.error("Hearings fetch error:", err))
      .finally(() => setLoadingHearings(false));
  }, [hearingDate]);

  const handleDateChange = (days) => {
    const next = new Date(hearingDate);
    next.setDate(next.getDate() + days);
    setHearingDate(next);
  };

  // Tracked Bills only means something with an individual or Workspace subscription. While access is
  // still loading, a signed-in user gets the subscriber layout so it doesn't jump once it resolves.
  const isSubscriber = !!auth?.user && (accessLoading || individualSubscription || isWorkspaceMember);

  const hearingsWidget = (
    <TodaysHearings
      data={hearings}
      loading={loadingHearings}
      selectedDate={hearingDate}
      onDateChange={handleDateChange}
      onSetDate={setHearingDate}
      trackedBills={trackedBills}
      teamTrackedBills={teamTrackedBills}
    />
  );

  const rulemakingWidget = (
    <UpcomingRulemaking
      data={rulemaking}
      loading={loadingRulemaking}
      workspace={workspaceData}
      userId={auth?.user?.user_id}
      onTaskCreated={handleRulemakingTaskCreated}
      onTaskUpdated={handleRulemakingTaskUpdated}
      onTaskDeleted={handleRulemakingTaskDeleted}
    />
  );

  return (
    <div className="dashboard-page">
      <PageTitle title="Dashboard | 'elloGovna" />
      <div className="container-fluid px-3">
        {/* Page header */}
        <div className="dashboard-page-header d-flex align-items-baseline gap-3 mb-4">
          <h1 className="dashboard-title mb-0">Dashboard</h1>
          <span className="dashboard-date">{todayLabel()}</span>
          <a
            href="https://calegislation.lc.ca.gov/Advocates/faces/index.xhtml"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm dashboard-portal-btn ms-auto"
          >
            <i className="" />Position Letter Portal
          </a>
        </div>

        {/* Row 1: Workspace (left) + Counts (right) for members; Counts + Promo for non-subscribers; Counts alone otherwise */}
        {isWorkspaceMember ? (
          <div className="row g-3 mb-3">
            <div className="col-lg-4">
              <WorkspaceWidget data={workspaceData} loading={loadingWorkspace} userId={auth?.user?.user_id} onTaskCreated={handleModalTaskCreated} onTaskEdited={handleTaskEdited} />
            </div>
            <div className="col-lg-8">
              <LegislativeCounts data={counts} loading={loadingCounts} tracking={tracking} fixedHeight />
            </div>
          </div>
        ) : showPromo ? (
          <div className="row g-3 mb-3">
            <div className="col-lg-9">
              <LegislativeCounts data={counts} loading={loadingCounts} tracking={tracking} />
            </div>
            <div className="col-lg-3">
              <SubscriptionPromo variant={promoVariant} />
            </div>
          </div>
        ) : (
          <LegislativeCounts data={counts} loading={loadingCounts} tracking={tracking} />
        )}

        {isSubscriber ? (
          <>
            {/* Row 2: Tracked Bills + Hearings, two columns on desktop */}
            <div className="row g-3">
              <div className="col-12 col-lg-6">
                <TrackedBillsStatus data={trackedBillsStatus} loading={loadingTrackedBillsStatus} />
              </div>
              <div className="col-12 col-lg-6">{hearingsWidget}</div>
            </div>

            {/* Row 3: Upcoming rulemaking (two thirds) beside Headlines + Top Posts stacked (one third) */}
            <div className="row g-3 mt-0">
              <div className="col-12 col-lg-8">
                <div className="rulemaking-slot">{rulemakingWidget}</div>
              </div>
              <div className="col-12 col-lg-4 d-flex flex-column gap-3">
                <TopHeadlines data={headlines} loading={loadingHeadlines} />
                <TopPosts data={topPosts} loading={loadingTopPosts} />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Non-subscribers have no Tracked Bills: Hearings + Upcoming Rulemaking take Row 2, Headlines + Top Posts the row below */}
            <div className="row g-3">
              <div className="col-12 col-lg-6">{hearingsWidget}</div>
              <div className="col-12 col-lg-6">{rulemakingWidget}</div>
            </div>
            <div className="row g-3 mt-0">
              <div className="col-12 col-lg-6">
                <TopHeadlines data={headlines} loading={loadingHeadlines} />
              </div>
              <div className="col-12 col-lg-6">
                <TopPosts data={topPosts} loading={loadingTopPosts} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
